import SocketService, { NAMESPACES, SocketRequest } from '../SocketService';

/**
 * Universal Socket Service
 * Handles all real-time events across the application including:
 * - Post events (create, reactions, saved, shared)
 * - Universal Comment Section (comments, reactions, replies on any target type)
 * - Database events (company/user new figures, insertions)
 * - User activity events
 */

// Singleton instance
let instance = null;

/**
 * Event Categories for Universal Socket
 */
export const UniversalEventTypes = {
  // Post Events
  POST: {
    CREATED: 'feed:user:post_created',           // User you follow created a post
    REACTED: 'post:reacted',           // Someone reacted to your post
    SAVED: 'post:saved',               // Someone saved your post
    SHARED: 'post:shared',             // Someone shared your post
    UPDATED: 'post:updated',           // Post content updated
    DELETED: 'post:deleted',           // Post deleted
    NEW_COMMENT: 'post:new_comment',   // New comment on your post
  },

  // Universal Comment Section Events
  COMMENT: {
    CREATED: 'comment:created',        // New comment on any target
    REACTED: 'comment:reacted',        // Reaction to your comment
    REPLIED: 'comment:replied',        // Reply to your comment
    UPDATED: 'comment:updated',        // Comment edited
    DELETED: 'comment:deleted',        // Comment deleted
    PINNED: 'comment:pinned',          // Comment pinned by author
  },

  // Database / Figure Events
  DATABASE: {
    COMPANY_FIGURE_RELEASED: 'feed:company:figure_released',  // Company released new figure (following)
    USER_FIGURE_RELEASED: 'feed:user:figure_released',        // User released created figure (following)
    USER_FIGURE_INSERTED: 'feed:user:figure_inserted',        // User inserted figure to database (following)
    FIGURE_UPDATED: 'db:figure:updated',                  // Figure data updated
    FIGURE_DELETED: 'db:figure:deleted',                  // Figure removed from database
  },

  // User Activity Events
  USER: {
    FOLLOWED: 'user:followed_you',         // Someone followed you
    UNFOLLOWED: 'user:unfollowed_you',     // Someone unfollowed you
    PROFILE_UPDATED: 'user:profile_updated',
    MENTIONED: 'user:mentioned_you',       // You were mentioned somewhere
  },

  // System Events
  SYSTEM: {
    CONNECTED: 'system:connected',
    DISCONNECTED: 'system:disconnected',
    RECONNECTED: 'system:reconnected',
    ERROR: 'system:error',
  }
};

/**
 * Target Types for Comments
 */
export const CommentTargetTypes = {
  POST: 'post',
  ARTICLE: 'article',
  DISCUSSION: 'discussion',
  COMPANY: 'company',
  GROUP: 'group',
  FIGURE: 'figure',
  CUSTOM: 'custom',
};

/**
 * Reaction Types
 */
export const ReactionTypes = {
  LIKE: 'like',
  LOVE: 'love',
  HAHA: 'haha',
  WOW: 'wow',
  SAD: 'sad',
  ANGRY: 'angry',
  HELPFUL: 'helpful',
  INSIGHTFUL: 'insightful',
};

class UniversalSocketService {
  constructor() {
    if (instance) {
      return instance;
    }

    this.socket = new SocketService(NAMESPACES.UNIVERSAL);
    this.connectionPromise = null;
    this.isInitialized = false;
    this.listenersSetup = false;
    this.listenersSetupInProgress = false;
    this.cleanupFns = [];

    // Active subscriptions tracking
    this.activeSubscriptions = new Set();

    // Listener registry - organized by category
    this.listeners = {
      // Post listeners
      onPostCreated: [],
      onPostReacted: [],
      onPostSaved: [],
      onPostShared: [],
      onPostUpdated: [],
      onPostDeleted: [],

      // Comment listeners
      onCommentCreated: [],
      onCommentReacted: [],
      onCommentReplied: [],
      onCommentUpdated: [],
      onCommentDeleted: [],
      onCommentPinned: [],

      // Database listeners
      onCompanyFigureReleased: [],
      onUserFigureReleased: [],
      onUserFigureInserted: [],
      onFigureUpdated: [],
      onFigureDeleted: [],

      // User listeners
      onUserFollowed: [],
      onUserUnfollowed: [],
      onUserProfileUpdated: [],
      onUserMentioned: [],

      // System listeners
      onConnected: [],
      onDisconnected: [],
      onReconnected: [],
      onError: [],
    };

    // Auto-initialize on creation
    this.initialize().catch(error => {
      console.error('❌ UniversalSocketService auto-initialization failed:', error);
    });

    instance = this;
  }

  static getInstance() {
    if (!instance) {
      instance = new UniversalSocketService();
    }
    return instance;
  }

  async initialize() {
    if (this.isInitialized) {
      return;
    }

    try {
      await this.ensureConnected();
      this.isInitialized = true;
      console.log('✅ UniversalSocketService initialized');
    } catch (error) {
      console.error('❌ Failed to initialize UniversalSocketService:', error);
      this.isInitialized = false;
      this.connectionPromise = null;
      throw error;
    }
  }

  async ensureConnected() {
    if (!this.connectionPromise) {
      this.connectionPromise = this.socket.connect()
        .then(() => {
          console.log('✅ UniversalSocketService: Connected');
          this.setupEventListeners();
          this.resubscribeToActiveTopics();
        })
        .catch(error => {
          console.error('❌ UniversalSocketService: Failed to connect:', error);
          this.connectionPromise = null;
          throw error;
        });
    }
    return this.connectionPromise;
  }

  async connect() {
    return this.ensureConnected();
  }

  async disconnect(destroy = false) {
    try {
      // Clean up all listeners
      this.cleanupFns.forEach(cleanup => {
        try {
          cleanup();
        } catch (error) {
          // Ignore cleanup errors
        }
      });
      this.cleanupFns = [];

      // Clear all registered listeners
      Object.keys(this.listeners).forEach(key => {
        this.listeners[key] = [];
      });

      await this.socket.disconnect();
      this.connectionPromise = null;
      this.isInitialized = false;
      this.listenersSetup = false;
      this.listenersSetupInProgress = false;
      this.activeSubscriptions.clear();

      if (destroy) {
        instance = null;
      }
    } catch (error) {
      console.error('❌ Error disconnecting UniversalSocketService:', error);
      throw error;
    }
  }

  generateID() {
    return Math.random().toString(36).substring(2, 9);
  }

  addListener(listener, callback) {
    const id = this.generateID();
    if (!Array.isArray(this.listeners[listener])) {
      this.listeners[listener] = [];
    }
    this.listeners[listener].push({ id, callback });
    return id;
  }

  removeListener(listener, id) {
    if (!Array.isArray(this.listeners[listener])) return;
    this.listeners[listener] = this.listeners[listener].filter((x) => x.id !== id);
  }

  triggerListener(listener, data) {
    const list = this.listeners?.[listener];
    if (!Array.isArray(list) || list.length === 0) return;
    list.forEach(({ callback }) => {
      try {
        callback(data);
      } catch (error) {
        console.error(`❌ Error in ${listener} callback:`, error);
      }
    });
  }

  setupEventListeners() {
    if (!this.socket?.socket) return;

    if (this.listenersSetup || this.listenersSetupInProgress) {
      console.log('ℹ️ UniversalSocketService listeners already setup, skipping...');
      return;
    }

    // mark immediately to avoid races between auto-initialize() and external connect()
    this.listenersSetup = true;
    this.listenersSetupInProgress = true;

    // Clear existing cleanup functions
    this.cleanupFns.forEach(cleanup => {
      try {
        cleanup();
      } catch (error) {
        // Ignore cleanup errors
      }
    });
    this.cleanupFns = [];

    // ==================== POST EVENTS ====================

    // Post created by user you follow
    const cleanupPostCreated = this.socket.on(UniversalEventTypes.POST.CREATED, (data) => {
      console.log('📰 post:created received:', data);
      this.triggerListener('onPostCreated', {
        postId: data.postId,
        authorId: data.authorId,
        authorName: data.authorName,
        content: data.content,
        createdAt: data.createdAt,
        metadata: data.metadata,
      });
    });
    this.cleanupFns.push(cleanupPostCreated);

    // Someone reacted to your post
    const cleanupPostReacted = this.socket.on(UniversalEventTypes.POST.REACTED, (data) => {
      console.log('😀 post:reacted received:', data);
      this.triggerListener('onPostReacted', {
        postId: data.postId,
        reactorId: data.reactorId,
        reactorName: data.reactorName,
        reactionType: data.reactionType,
        reactedAt: data.reactedAt,
        reactionCount: data.reactionCount,
      });
    });
    this.cleanupFns.push(cleanupPostReacted);

    // Someone saved your post
    const cleanupPostSaved = this.socket.on(UniversalEventTypes.POST.SAVED, (data) => {
      console.log('🔖 post:saved received:', data);
      this.triggerListener('onPostSaved', {
        postId: data.postId,
        saverId: data.saverId,
        saverName: data.saverName,
        savedAt: data.savedAt,
        saveCount: data.saveCount,
      });
    });
    this.cleanupFns.push(cleanupPostSaved);

    // Someone shared your post
    const cleanupPostShared = this.socket.on(UniversalEventTypes.POST.SHARED, (data) => {
      console.log('📤 post:shared received:', data);
      this.triggerListener('onPostShared', {
        postId: data.postId,
        sharerId: data.sharerId,
        sharerName: data.sharerName,
        sharedAt: data.sharedAt,
        shareCount: data.shareCount,
        shareTarget: data.shareTarget, // where it was shared to
      });
    });
    this.cleanupFns.push(cleanupPostShared);

    // Post updated
    const cleanupPostUpdated = this.socket.on(UniversalEventTypes.POST.UPDATED, (data) => {
      console.log('✏️ post:updated received:', data);
      this.triggerListener('onPostUpdated', {
        postId: data.postId,
        authorId: data.authorId,
        updatedContent: data.content,
        updatedAt: data.updatedAt,
        previousContent: data.previousContent,
      });
    });
    this.cleanupFns.push(cleanupPostUpdated);

    // Post deleted
    const cleanupPostDeleted = this.socket.on(UniversalEventTypes.POST.DELETED, (data) => {
      console.log('🗑️ post:deleted received:', data);
      this.triggerListener('onPostDeleted', {
        postId: data.postId,
        authorId: data.authorId,
        deletedAt: data.deletedAt,
        reason: data.reason,
      });
    });
    this.cleanupFns.push(cleanupPostDeleted);

    // ==================== COMMENT EVENTS ====================

    // New comment on any target (Post, Article, Discussion, Company, Group, Custom)
    const cleanupCommentCreated = this.socket.on(UniversalEventTypes.COMMENT.CREATED, (data) => {
      console.log('💬 comment:created received:', data);
      this.triggerListener('onCommentCreated', {
        commentId: data.commentId,
        targetId: data.targetId,
        targetType: data.targetType, // post, article, discussion, company, group, custom
        authorId: data.authorId,
        authorName: data.authorName,
        authorAvatar: data.authorAvatar,
        content: data.content,
        parentCommentId: data.parentCommentId, // null if top-level
        createdAt: data.createdAt,
        mentions: data.mentions || [],
        metadata: data.metadata,
      });
    });
    this.cleanupFns.push(cleanupCommentCreated);

    // Someone reacted to your comment
    const cleanupCommentReacted = this.socket.on(UniversalEventTypes.COMMENT.REACTED, (data) => {
      console.log('😀 comment:reacted received:', data);
      const reactor = data?.reactor || {};
      this.triggerListener('onCommentReacted', {
        commentId: data.commentId,
        targetId: data.targetId,
        targetType: data.targetType,
        reactorId: reactor?.id || data.reactorId,
        reactorName: reactor?.username || data.reactorName,
        reactorAvatar: reactor?.avatar,
        reactor,
        reactionType: data.reactionType,
        reactionAction: data.reactionAction,
        previousReactionType: data.previousReactionType,
        reactedAt: data.reactedAt,
        reactionCount: data.reactionCount,
      });
    });
    this.cleanupFns.push(cleanupCommentReacted);

    // Reply to your comment
    const cleanupCommentReplied = this.socket.on(UniversalEventTypes.COMMENT.REPLIED, (data) => {
      console.log('↩️ comment:replied received:', data);
      const replyAuthor = data?.replyAuthor || {};
      this.triggerListener('onCommentReplied', {
        // backend uses replyCommentId; keep commentId for older listeners
        commentId: data.replyCommentId || data.commentId,
        parentCommentId: data.parentCommentId,
        targetId: data.targetId,
        targetType: data.targetType,
        authorId: replyAuthor?.id || data.authorId,
        authorName: replyAuthor?.username || data.authorName,
        authorAvatar: replyAuthor?.avatar,
        content: data.replyContent || data.content,
        createdAt: data.repliedAt || data.createdAt,
        mentions: data.mentions || [],

        // expose richer new shape too
        replyCommentId: data.replyCommentId,
        replyAuthor,
        repliedAt: data.repliedAt,
        parentCommentPreview: data.parentCommentPreview,
      });
    });
    this.cleanupFns.push(cleanupCommentReplied);

    // Comment updated/edited
    const cleanupCommentUpdated = this.socket.on(UniversalEventTypes.COMMENT.UPDATED, (data) => {
      console.log('✏️ comment:updated received:', data);
      this.triggerListener('onCommentUpdated', {
        commentId: data.commentId,
        targetId: data.targetId,
        targetType: data.targetType,
        authorId: data.authorId,
        updatedContent: data.content,
        updatedAt: data.updatedAt,
        isEdited: data.isEdited,
        editHistory: data.editHistory,
      });
    });
    this.cleanupFns.push(cleanupCommentUpdated);

    // Comment deleted
    const cleanupCommentDeleted = this.socket.on(UniversalEventTypes.COMMENT.DELETED, (data) => {
      console.log('🗑️ comment:deleted received:', data);
      this.triggerListener('onCommentDeleted', {
        commentId: data.commentId,
        targetId: data.targetId,
        targetType: data.targetType,
        deletedBy: data.deletedBy, // author, moderator, admin
        deletedAt: data.deletedAt,
        reason: data.reason,
      });
    });
    this.cleanupFns.push(cleanupCommentDeleted);

    // Comment pinned
    const cleanupCommentPinned = this.socket.on(UniversalEventTypes.COMMENT.PINNED, (data) => {
      console.log('📌 comment:pinned received:', data);
      this.triggerListener('onCommentPinned', {
        commentId: data.commentId,
        targetId: data.targetId,
        targetType: data.targetType,
        pinnedBy: data.pinnedBy,
        pinnedAt: data.pinnedAt,
        isPinned: data.isPinned, // true = pinned, false = unpinned
      });
    });
    this.cleanupFns.push(cleanupCommentPinned);

    // ==================== DATABASE EVENTS ====================

    // Company released new figure (you follow this company)
    const cleanupCompanyFigureReleased = this.socket.on(UniversalEventTypes.DATABASE.COMPANY_FIGURE_RELEASED, (data) => {
      console.log('🏢 db:company:figure_released received:', data);
      this.triggerListener('onCompanyFigureReleased', {
        companyId: data.companyId,
        companyName: data.companyName,
        figureId: data.figureId,
        figureName: data.figureName,
        figureSeries: data.figureSeries,
        releaseDate: data.releaseDate,
        images: data.images || [],
        price: data.price,
        metadata: data.metadata,
      });
    });
    this.cleanupFns.push(cleanupCompanyFigureReleased);

    // User released created figure (not draft, you follow this user)
    const cleanupUserFigureReleased = this.socket.on(UniversalEventTypes.DATABASE.USER_FIGURE_RELEASED, (data) => {
      console.log('👤 db:user:figure_released received:', data);
      this.triggerListener('onUserFigureReleased', {
        userId: data.userId,
        username: data.username,
        figureId: data.figureId,
        figureName: data.figureName,
        figureSeries: data.figureSeries,
        releaseType: data.releaseType, // official, custom, etc.
        releasedAt: data.releasedAt,
        images: data.images || [],
        description: data.description,
        metadata: data.metadata,
      });
    });
    this.cleanupFns.push(cleanupUserFigureReleased);

    // User inserted figure to database (you follow this user)
    const cleanupUserFigureInserted = this.socket.on(UniversalEventTypes.DATABASE.USER_FIGURE_INSERTED, (data) => {
      console.log('📥 db:user:figure_inserted received:', data);
      this.triggerListener('onUserFigureInserted', {
        userId: data.userId,
        username: data.username,
        figureId: data.figureId,
        figureName: data.figureName,
        insertedAt: data.insertedAt,
        collectionLocation: data.collectionLocation,
        condition: data.condition,
        purchasePrice: data.purchasePrice,
        notes: data.notes,
      });
    });
    this.cleanupFns.push(cleanupUserFigureInserted);

    // Figure data updated
    const cleanupFigureUpdated = this.socket.on(UniversalEventTypes.DATABASE.FIGURE_UPDATED, (data) => {
      console.log('🔄 db:figure:updated received:', data);
      this.triggerListener('onFigureUpdated', {
        figureId: data.figureId,
        updatedBy: data.updatedBy,
        updatedFields: data.updatedFields,
        previousValues: data.previousValues,
        updatedAt: data.updatedAt,
      });
    });
    this.cleanupFns.push(cleanupFigureUpdated);

    // Figure deleted from database
    const cleanupFigureDeleted = this.socket.on(UniversalEventTypes.DATABASE.FIGURE_DELETED, (data) => {
      console.log('🗑️ db:figure:deleted received:', data);
      this.triggerListener('onFigureDeleted', {
        figureId: data.figureId,
        deletedBy: data.deletedBy,
        deletedAt: data.deletedAt,
        reason: data.reason,
      });
    });
    this.cleanupFns.push(cleanupFigureDeleted);

    // ==================== USER EVENTS ====================

    // Someone followed you
    const cleanupUserFollowed = this.socket.on(UniversalEventTypes.USER.FOLLOWED, (data) => {
      console.log('➕ user:followed received:', data);
      this.triggerListener('onUserFollowed', {
        followerId: data.followerId,
        followerName: data.followerName,
        followerAvatar: data.followerAvatar,
        followedAt: data.followedAt,
        isMutual: data.isMutual,
      });
    });
    this.cleanupFns.push(cleanupUserFollowed);

    // Someone unfollowed you
    const cleanupUserUnfollowed = this.socket.on(UniversalEventTypes.USER.UNFOLLOWED, (data) => {
      console.log('➖ user:unfollowed received:', data);
      this.triggerListener('onUserUnfollowed', {
        followerId: data.followerId,
        followerName: data.followerName,
        unfollowedAt: data.unfollowedAt,
      });
    });
    this.cleanupFns.push(cleanupUserUnfollowed);

    // User profile updated (someone you follow)
    const cleanupUserProfileUpdated = this.socket.on(UniversalEventTypes.USER.PROFILE_UPDATED, (data) => {
      console.log('👤 user:profile_updated received:', data);
      this.triggerListener('onUserProfileUpdated', {
        userId: data.userId,
        username: data.username,
        updatedFields: data.updatedFields,
        avatarUrl: data.avatarUrl,
        updatedAt: data.updatedAt,
      });
    });
    this.cleanupFns.push(cleanupUserProfileUpdated);

    // You were mentioned
    const cleanupUserMentioned = this.socket.on(UniversalEventTypes.USER.MENTIONED, (data) => {
      console.log('🔔 user:mentioned received:', data);
      this.triggerListener('onUserMentioned', {
        mentionId: data.mentionId,
        mentionedBy: data.mentionedBy,
        mentionedByName: data.mentionedByName,
        targetId: data.targetId,
        targetType: data.targetType, // post, comment, article, etc.
        context: data.context,
        mentionedAt: data.mentionedAt,
      });
    });
    this.cleanupFns.push(cleanupUserMentioned);

    // ==================== SYSTEM EVENTS ====================

    // Disconnect
    const cleanupDisconnect = this.socket.on('disconnect', (reason) => {
      console.log('🔌 UniversalSocket disconnected. Reason:', reason);
      this.connectionPromise = null;
      this.triggerListener('onDisconnected', { reason, timestamp: new Date().toISOString() });
    });
    this.cleanupFns.push(cleanupDisconnect);

    // Reconnect
    const cleanupReconnect = this.socket.on('reconnect', (attemptNumber) => {
      console.log(`🔄 UniversalSocket reconnected after ${attemptNumber} attempts`);
      this.resubscribeToActiveTopics();
      this.triggerListener('onReconnected', { attemptNumber, timestamp: new Date().toISOString() });
    });
    this.cleanupFns.push(cleanupReconnect);

    // Catch-all for debugging
    const cleanupAny = this.socket.socket.onAny((event, ...args) => {
      if (!Object.values(UniversalEventTypes.POST).includes(event) &&
          !Object.values(UniversalEventTypes.COMMENT).includes(event) &&
          !Object.values(UniversalEventTypes.DATABASE).includes(event) &&
          !Object.values(UniversalEventTypes.USER).includes(event) &&
          event !== 'disconnect' && event !== 'reconnect') {
        console.log(`[UNIVERSAL SOCKET ANY] Event received: ${event}`, args);
      }
    });
    this.cleanupFns.push(cleanupAny);

    this.listenersSetupInProgress = false;
    console.log('✅ UniversalSocketService event listeners setup complete');
  }

  // ==================== EMIT HELPERS ====================

  async safeEmit(event, data) {
    await this.ensureConnected();
    return this.emit(event, data);
  }

  emit(event, data) {
    return this.socket.emit(event, data);
  }

  // ==================== SUBSCRIPTION MANAGEMENT ====================

  /**
   * Subscribe to a specific topic/target for real-time updates
   */
  async subscribe(topic, targetId) {
    await this.ensureConnected();
    const subscriptionKey = `${topic}:${targetId}`;
    this.activeSubscriptions.add(subscriptionKey);

    this.emit('subscribe', {
      topic,
      targetId,
      timestamp: new Date().toISOString(),
    });

    console.log(`📌 Subscribed to ${subscriptionKey}`);
  }

  /**
   * Unsubscribe from a specific topic/target
   */
  async unsubscribe(topic, targetId) {
    const subscriptionKey = `${topic}:${targetId}`;
    this.activeSubscriptions.delete(subscriptionKey);

    this.emit('unsubscribe', {
      topic,
      targetId,
      timestamp: new Date().toISOString(),
    });

    console.log(`📤 Unsubscribed from ${subscriptionKey}`);
  }

  /**
   * Resubscribe to all active topics after reconnection
   */
  resubscribeToActiveTopics() {
    if (this.activeSubscriptions.size === 0) return;

    console.log(`🔄 Resubscribing to ${this.activeSubscriptions.size} topics...`);

    this.activeSubscriptions.forEach(subscriptionKey => {
      const [topic, targetId] = subscriptionKey.split(':');
      this.emit('subscribe', {
        topic,
        targetId,
        timestamp: new Date().toISOString(),
      });
    });
  }

  // ==================== POST ACTIONS (Emitters) ====================

  /**
   * React to a post
   */
  async reactToPost(postId, reactionType) {
    await this.safeEmit(UniversalEventTypes.POST.REACTED, {
      postId,
      reactionType,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Save a post
   */
  async savePost(postId) {
    await this.safeEmit(UniversalEventTypes.POST.SAVED, {
      postId,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Share a post
   */
  async sharePost(postId, shareTarget) {
    await this.safeEmit(UniversalEventTypes.POST.SHARED, {
      postId,
      shareTarget,
      timestamp: new Date().toISOString(),
    });
  }

  // ==================== COMMENT ACTIONS (Emitters) ====================

  /**
   * Create a comment on any target type
   */
  async createComment(targetId, targetType, content, parentCommentId = null, mentions = []) {
    await this.safeEmit(UniversalEventTypes.COMMENT.CREATED, {
      targetId,
      targetType,
      content,
      parentCommentId,
      mentions,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * React to a comment
   */
  async reactToComment(commentId, targetId, targetType, reactionType) {
    await this.safeEmit(UniversalEventTypes.COMMENT.REACTED, {
      commentId,
      targetId,
      targetType,
      reactionType,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Reply to a comment
   */
  async replyToComment(parentCommentId, targetId, targetType, content, mentions = []) {
    await this.safeEmit(UniversalEventTypes.COMMENT.REPLIED, {
      parentCommentId,
      targetId,
      targetType,
      content,
      mentions,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Update/edit a comment
   */
  async updateComment(commentId, targetId, targetType, content) {
    await this.safeEmit(UniversalEventTypes.COMMENT.UPDATED, {
      commentId,
      targetId,
      targetType,
      content,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Delete a comment
   */
  async deleteComment(commentId, targetId, targetType) {
    await this.safeEmit(UniversalEventTypes.COMMENT.DELETED, {
      commentId,
      targetId,
      targetType,
      timestamp: new Date().toISOString(),
    });
  }

  // ==================== LISTENER REGISTRATION (Public API) ====================

  // Post Listeners
  onPostCreated(callback) { return this._registerListener('onPostCreated', callback); }
  onPostReacted(callback) { return this._registerListener('onPostReacted', callback); }
  onPostSaved(callback) { return this._registerListener('onPostSaved', callback); }
  onPostShared(callback) { return this._registerListener('onPostShared', callback); }
  onPostUpdated(callback) { return this._registerListener('onPostUpdated', callback); }
  onPostDeleted(callback) { return this._registerListener('onPostDeleted', callback); }

  // Comment Listeners
  onCommentCreated(callback) { return this._registerListener('onCommentCreated', callback); }
  onCommentReacted(callback) { return this._registerListener('onCommentReacted', callback); }
  onCommentReplied(callback) { return this._registerListener('onCommentReplied', callback); }
  onCommentUpdated(callback) { return this._registerListener('onCommentUpdated', callback); }
  onCommentDeleted(callback) { return this._registerListener('onCommentDeleted', callback); }
  onCommentPinned(callback) { return this._registerListener('onCommentPinned', callback); }

  // Database Listeners
  onCompanyFigureReleased(callback) { return this._registerListener('onCompanyFigureReleased', callback); }
  onUserFigureReleased(callback) { return this._registerListener('onUserFigureReleased', callback); }
  onUserFigureInserted(callback) { return this._registerListener('onUserFigureInserted', callback); }
  onFigureUpdated(callback) { return this._registerListener('onFigureUpdated', callback); }
  onFigureDeleted(callback) { return this._registerListener('onFigureDeleted', callback); }

  // User Listeners
  onUserFollowed(callback) { return this._registerListener('onUserFollowed', callback); }
  onUserUnfollowed(callback) { return this._registerListener('onUserUnfollowed', callback); }
  onUserProfileUpdated(callback) { return this._registerListener('onUserProfileUpdated', callback); }
  onUserMentioned(callback) { return this._registerListener('onUserMentioned', callback); }

  // System Listeners
  onConnected(callback) { return this._registerListener('onConnected', callback); }
  onDisconnected(callback) { return this._registerListener('onDisconnected', callback); }
  onReconnected(callback) { return this._registerListener('onReconnected', callback); }
  onError(callback) { return this._registerListener('onError', callback); }

  _registerListener(listener, callback) {
    const id = this.addListener(listener, callback);
    return () => this.removeListener(listener, id);
  }

  // ==================== UTILITY ====================

  /**
   * Get current status
   */
  getStatus() {
    return {
      isInitialized: this.isInitialized,
      isConnected: this.socket?.isConnected || false,
      activeSubscriptions: Array.from(this.activeSubscriptions),
      listenersRegistered: Object.entries(this.listeners).reduce((acc, [key, value]) => {
        acc[key] = value.length;
        return acc;
      }, {}),
    };
  }

  /**
   * Remove all listeners (cleanup)
   */
  removeAllListeners() {
    Object.keys(this.listeners).forEach(key => {
      this.listeners[key] = [];
    });
    this.cleanupFns.forEach(cleanup => {
      try {
        cleanup();
      } catch (error) {
        // Ignore cleanup errors
      }
    });
    this.cleanupFns = [];
    this.listenersSetup = false;
    this.listenersSetupInProgress = false;
  }
}

// Export the class (default) and also the named class export for compatibility
export { UniversalSocketService };
export default UniversalSocketService;
